import { useEffect, useCallback, useRef } from 'react';

const useKeyboardShortcuts = (shortcuts, enabled = true) => {
  const pendingKey = useRef(null);
  const pendingTimeout = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (!enabled) return;

    // Ignore when typing in input/textarea/select
    const tag = e.target.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) {
      // Only allow Escape in inputs
      if (e.key === 'Escape') {
        e.target.blur();
      }
      return;
    }

    const key = e.key;
    const ctrl = e.ctrlKey || e.metaKey;

    // Handle Ctrl+K (universal search)
    if (ctrl && key === 'k') {
      e.preventDefault();
      const handler = shortcuts.find(s => s.keys === 'Ctrl+K');
      if (handler) handler.action();
      return;
    }

    // Handle two-key sequences (g then o, g then s, etc.)
    if (pendingKey.current) {
      const combo = `${pendingKey.current} ${key}`;
      pendingKey.current = null;
      clearTimeout(pendingTimeout.current);
      
      const handler = shortcuts.find(s => s.keys === combo);
      if (handler) {
        e.preventDefault();
        handler.action();
        return;
      }
    }

    // Start a two-key sequence
    if (key === 'g') {
      pendingKey.current = 'g';
      pendingTimeout.current = setTimeout(() => {
        pendingKey.current = null;
      }, 800);
      return;
    }

    // Handle single-key shortcuts
    const handler = shortcuts.find(s => s.keys === key || s.keys === e.key);
    if (handler) {
      e.preventDefault();
      handler.action();
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export default useKeyboardShortcuts;
